import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import styled from 'styled-components'
import axios from 'axios'

const EditTable = forwardRef((props, ref) => {
  const { history, getPerks, onboardingList, lang } = props

  const [data, setData] = useState([])
  const [drag, setDrag] = useState(false)
  const [isScroll, setIsScroll] = useState('')
  const [posY, setPosY] = useState(0)
  const [checked, setChecked] = useState([])

  const checkRef = useRef([])
  const curationRef = useRef()

  useEffect(() => {
    setData(onboardingList)
  }, [onboardingList])

  const handleCheck = e => {
    if (e.target.checked) {
      setChecked(checked.concat(Number(e.target.value)))
    } else {
      setChecked(checked.filter(id => id !== Number(e.target.value)))
    }
  }

  const dragDataInit = {
    target: null,
    index: -1,
    move_up: [],
    move_down: [],
    updateList: [],
  }

  const [dragData, setDragData] = useState(dragDataInit)

  const _dragStart = e => {
    if (!drag) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    setDragData({
      ...dragData,
      target: e.currentTarget,
      index: Number(e.currentTarget.dataset.index),
      updateList: [...data],
    })

    e.dataTransfer.setData('text/html', '')
    e.dataTransfer.effectAllowed = 'move'
  }

  const _dragEnter = e => {
    if (!drag) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const _dragged = Number(dragData.target.dataset.index)
    const _target = Number(e.currentTarget.dataset.index)
    let move_down = [...dragData.move_down]
    let move_up = [...dragData.move_up]

    if (_dragged > _target) {
      if (move_down.includes(_target)) {
        move_down = move_down.slice(0, move_down.indexOf(_target))
      } else {
        move_down = Array(_dragged - _target)
          .fill()
          .map((v, i) => i + _target)
          .reverse()
      }
      move_up = []
    } else if (_dragged < _target) {
      if (move_up.includes(_target)) {
        move_up = move_up.slice(0, move_up.indexOf(_target))
      } else {
        move_up = Array(_target - _dragged)
          .fill()
          .map((v, i) => _target - i)
          .reverse()
      }
      move_down = []
    } else {
      move_down = []
      move_up = []
    }

    setDragData({
      ...dragData,
      // updateList: updateData,
      index: _target,
      move_up,
      move_down,
    })
  }

  const _dragLeave = e => {
    if (!drag) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    if (e.currentTarget === dragData.target) {
      e.currentTarget.style.visibility = 'hidden'
    }
  }

  const _dragEnd = e => {
    if (!drag) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    let updateData = [...dragData.updateList]

    const changeOrder = (i, t) => {
      const target = updateData.splice(i, 1)[0]
      updateData.splice(t, 0, target)
    }

    const _dragged = Number(e.currentTarget.dataset.index)
    let _target = Number(dragData.index)

    if (dragData.move_up.length > 0) {
      _target = dragData.move_up[dragData.move_up.length - 1]
    } else if (dragData.move_down.length > 0) {
      _target = dragData.move_down[dragData.move_down.length - 1]
    } else {
      _target = _dragged
    }

    changeOrder(_dragged, _target)

    setDrag(false)
    setData(updateData)
    reorder(updateData)
    setDragData({
      ...dragData,
      move_down: [],
      move_up: [],
      updateList: [],
    })

    e.currentTarget.style.visibility = 'visible'
    e.dataTransfer.dropEffect = 'move'
  }

  const _dragCapture = async e => {
    if (!drag) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const posH = e.target.getBoundingClientRect().height
    const bottom = curationRef.current.getBoundingClientRect().bottom
    const top = curationRef.current.getBoundingClientRect().top

    setPosY(posH)

    if (e.clientY && e.clientY > top && e.clientY < bottom) {
      if (bottom - e.clientY < 100) {
        setIsScroll('down')
      } else if (e.clientY - top < 100) {
        setIsScroll('up')
      } else {
        setIsScroll('')
      }
    }
  }

  useEffect(() => {
    const scrollHeight = curationRef.current.scrollTop
    const posH = curationRef.current.getBoundingClientRect().height

    if (isScroll === 'down') {
      curationRef.current.scrollTo({
        top: scrollHeight + posH - posY * 2,
        behavior: 'smooth',
      })
    }

    if (isScroll === 'up') {
      curationRef.current.scrollTo({
        top: scrollHeight - posH + posY * 2,
        behavior: 'smooth',
      })
    }
  }, [isScroll])

  const reorderPromise = (id, order) => {
    const params = {
      id,
      order,
    }

    return new Promise(async (resolve, reject) => {
      await axios
        .put(`/new/webAdmin/v2/perks/onboarding/order`, params, {
          headers: {
            token: sessionStorage.getItem('token'),
          },
        })
        .then(res => {
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }

  const reorder = async list => {
    const result = await Promise.all(
      list.map((item, index) => reorderPromise(item.seq, index + 1))
    )

    if (result) {
      await getPerks()
    }
  }

  const clickDelete = async () => {
    if (checked.length > 0) {
      const params = {
        deleted: checked,
      }

      await axios
        .delete(`/new/webAdmin/v2/perks/onboarding`, {
          headers: {
            token: sessionStorage.getItem('token'),
          },
          data: params,
        })
        .then(() => {
          setChecked([])
          getPerks()
        })
    }
  }

  useImperativeHandle(ref, () => ({
    clickDelete,
  }))

  return (
    <Wrapper>
      <CollectionWrapper ref={curationRef}>
        {onboardingList.length > 0 &&
          onboardingList.map((item, index) => {
            let default_class = ''
            dragData.move_down.includes(index) && (default_class = 'move_down')
            dragData.move_up.includes(index) && (default_class = 'move_up')
            return (
              <CollectionItem
                key={index}
                data={item}
                data-index={index}
                draggable={true}
                onDragStart={_dragStart}
                onDragEnter={_dragEnter}
                onDragLeave={_dragLeave}
                onDragEnd={_dragEnd}
                onDragCapture={_dragCapture}
                className={default_class}
                disabled={item.status === 'N'}
              >
                <div style={{ width: '5%' }}>
                  <input
                    ref={el => (checkRef.current[index] = el)}
                    type="checkbox"
                    value={item.seq}
                    onChange={e => {
                      handleCheck(e)
                    }}
                  />
                </div>
                <div style={{ width: '10%' }}>#{index + 1}</div>
                <div
                  style={{
                    width: '35%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    fontWeight: 'bold',
                  }}
                >
                  <div style={{ alignItems: 'end' }}>
                    {lang === 'ja'
                      ? item.title_ja !== 'null'
                        ? item.title_ja
                        : ''
                      : lang === 'ko'
                      ? item.title_ko !== 'null'
                        ? item.title_ko
                        : ''
                      : lang === 'en' && item.title_en !== 'null'
                      ? item.title_en
                      : lang === 'ru' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ru')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ru')[0]
                            .title
                        : ''
                      : lang === 'pt' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'pt')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'pt')[0]
                            .title
                        : ''
                      : lang === 'es' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'es')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'es')[0]
                            .title
                        : ''
                      : lang === 'id' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'id')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'id')[0]
                            .title
                        : ''
                      : lang === 'zh-TW' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'zh-TW')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'zh-TW')[0]
                            .title
                        : ''
                      : lang === 'ms' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ms')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ms')[0]
                            .title
                        : ''
                      : lang === 'vi' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'vi')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'vi')[0]
                            .title
                        : ''
                      : lang === 'ar' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ar')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ar')[0]
                            .title
                        : ''
                      : lang === 'hi' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'hi')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'hi')[0]
                            .title
                        : ''
                      : ''}
                    ({item.keyword ? item.keyword : ''})
                  </div>
                  <div style={{ fontWeight: 'normal', alignItems: 'start' }}>
                    {lang === 'ja'
                      ? item.description_ja !== 'null'
                        ? item.description_ja
                        : ''
                      : lang === 'ko'
                      ? item.description_ko !== 'null'
                        ? item.description_ko
                        : ''
                      : lang === 'en' && item.description_en !== 'null'
                      ? item.description_en
                      : lang === 'ru' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ru')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ru')[0]
                            .description
                        : ''
                      : lang === 'pt' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'pt')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'pt')[0]
                            .description
                        : ''
                      : lang === 'es' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'es')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'es')[0]
                            .description
                        : ''
                      : lang === 'id' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'id')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'id')[0]
                            .description
                        : ''
                      : lang === 'zh-TW' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'zh-TW')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'zh-TW')[0]
                            .description
                        : ''
                      : lang === 'ms' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ms')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ms')[0]
                            .description
                        : ''
                      : lang === 'vi' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'vi')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'vi')[0]
                            .description
                        : ''
                      : lang === 'ar' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'ar')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'ar')[0]
                            .description
                        : ''
                      : lang === 'hi' && item.onboardingInfo
                      ? item.onboardingInfo.filter(i => i.lang === 'hi')[0]
                        ? item.onboardingInfo.filter(i => i.lang === 'hi')[0]
                            .description
                        : ''
                      : ''}
                  </div>
                </div>
                <div style={{ width: '10%' }}>{`+${item.coin} coin`}</div>
                <div style={{ width: '10%' }}>{`${item.maxCount}번`}</div>
                <div style={{ width: '15%' }}>
                  <img
                    src={item.img}
                    alt=""
                    style={{ width: 'auto', height: '100%' }}
                  />
                </div>
                <div style={{ width: '15%', justifyContent: 'flex-end' }}>
                  <button
                    id="edit-button"
                    onClick={e => {
                      history.push('/Perks/Edit', { item })
                    }}
                    style={{ marginRight: '15px' }}
                  >
                    Edit
                  </button>
                  <img
                    id="drag"
                    src="https://img.stipop.io/image/account/hamburger_menu.png"
                    alt=""
                    draggable={false}
                    onMouseDown={() => {
                      setDrag(true)
                    }}
                  />
                </div>
              </CollectionItem>
            )
          })}
      </CollectionWrapper>
    </Wrapper>
  )
})

export default EditTable

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  margin-left: 10px;
  padding-right: 10px;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`

const CollectionWrapper = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`

const CollectionItem = styled.div`
  width: 100%;
  min-width: 700px;
  height: 70px;
  background-color: ${props => (props.disabled ? '#ededed' : '#fff')};
  border-radius: 10px;
  border: 1px solid #000;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 15px;
  box-sizing: border-box;
  position: relative;
  opacity: 0.999;

  div {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    #edit-button {
      height: 30px;
      border: none;
      background-color: #007ffb;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 10px;
      box-sizing: border-box;
      border-radius: 5px;

      &:hover {
        opacity: 0.8;
      }
    }

    #drag {
      width: 25px;
      height: 20px;
      cursor: pointer;
      z-index: 2;
    }

    button {
      margin-right: 10px;
    }
  }

  input[type='checkbox'] {
    width: 20px;
    height: 20px;
  }

  &.move_up {
    transform: translateY(-70px);
    z-index: 1;
  }
  &.move_down {
    transform: translateY(70px);
    z-index: 1;
  }
`
